import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  Snowflake, 
  ArrowLeft, 
  Shield,
  Mail,
  Phone,
  MapPin,
  ExternalLink
} from "lucide-react";

const Datenschutz = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-sky-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-lg border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 text-slate-600 hover:text-sky-500 transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span>Zurück zur Startseite</span>
          </Link>
          <div className="flex items-center gap-3">
            <Snowflake className="w-6 h-6 text-sky-500" />
            <span className="font-semibold text-slate-900" style={{ fontFamily: 'Playfair Display' }}>
              Grömitzer Eiszeit
            </span>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        {/* Title */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-sky-100 mb-6">
            <Shield className="w-8 h-8 text-sky-600" />
          </div>
          <h1 
            className="text-4xl md:text-5xl font-medium tracking-tight text-slate-900 mb-4"
            style={{ fontFamily: 'Playfair Display' }}
          >
            Datenschutzerklärung
          </h1>
          <p className="text-lg text-slate-600">Grömitzer Eiszeit</p>
        </div>

        <Card className="border-0 shadow-xl">
          <CardContent className="p-8 md:p-12 prose prose-slate max-w-none">
            
            {/* 1. Verantwortlicher */}
            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4 flex items-center gap-3" style={{ fontFamily: 'Playfair Display' }}>
                <span className="flex items-center justify-center w-8 h-8 rounded-full bg-sky-100 text-sky-600 text-sm font-bold">1</span>
                Verantwortlicher
              </h2>
              <p className="text-slate-600 mb-4">
                Verantwortlich für die Datenverarbeitung auf dieser Website im Sinne der Datenschutz-Grundverordnung (DSGVO) ist:
              </p>
              <div className="bg-slate-50 rounded-xl p-6">
                <p className="font-semibold text-slate-900 mb-2">Grömitzer Eiszeit</p>
                <p className="text-slate-600 mb-1">Inhaber: Majid Kalantary</p>
                <div className="flex items-start gap-2 text-slate-600 mb-1">
                  <MapPin className="w-4 h-4 mt-1 text-sky-500" />
                  <span>Niedernstr. 11, 23628 Krummesse</span>
                </div>
                <div className="flex items-center gap-2 text-slate-600 mb-1">
                  <Phone className="w-4 h-4 text-sky-500" />
                  <span>0155 61642249</span>
                </div>
                <div className="flex items-center gap-2 text-slate-600">
                  <Mail className="w-4 h-4 text-sky-500" />
                  <span>info@groemitzereiszeit.de</span>
                </div>
              </div>
            </section>

            {/* Hinweise Google Analytics */}
            <section className="mb-10 bg-sky-50 rounded-xl p-6 border border-sky-100">
              <h3 className="text-lg font-semibold text-slate-900 mb-3">
                Hinweise zur Datenverarbeitung im Zusammenhang mit Google Analytics
              </h3>
              <p className="text-slate-600 text-sm mb-3">
                Diese Website benutzt Google Analytics, einen Webanalysedienst der Google Ireland Limited, Gordon House, Barrow Street, Dublin 4, Irland. Sofern eine Datenverarbeitung außerhalb des Europäischen Wirtschaftsraumes oder der Schweiz erfolgt, wird diese durch Google LLC, 1600 Amphitheatre Parkway, Mountain View, CA 94043, USA durchgeführt. Google Ireland Limited und Google LLC werden nachfolgend gemeinsam „Google" genannt.
              </p>
              <p className="text-slate-600 text-sm mb-3">
                Die technische Bereitstellung dieser Website (z. B. Hosting, Auslieferung der Inhalte sowie ggf. Einbindung von Analyse-/Tracking-Funktionen) erfolgt über Emergent.
              </p>
              <p className="text-slate-600 text-sm">
                Der Betreiber der Website ist Verantwortlicher im Sinne der DSGVO für die Verarbeitung personenbezogener Daten im Rahmen des Betriebs dieser Website, soweit in dieser Datenschutzerklärung nichts Abweichendes geregelt ist.
              </p>
            </section>

            {/* 2. Zweck der Verarbeitung */}
            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4 flex items-center gap-3" style={{ fontFamily: 'Playfair Display' }}>
                <span className="flex items-center justify-center w-8 h-8 rounded-full bg-sky-100 text-sky-600 text-sm font-bold">2</span>
                Zweck der Verarbeitung
              </h2>
              <p className="text-slate-600 mb-3">
                Google Analytics wird eingesetzt, um die Nutzung der Website auszuwerten, Reports über Websiteaktivitäten zu erstellen sowie Inhalte und Nutzerfreundlichkeit dieser Website zu optimieren.
              </p>
              <p className="text-slate-600 mb-2">
                <strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse).
              </p>
              <p className="text-slate-600">
                Das berechtigte Interesse liegt in der Analyse der Nutzung, der Optimierung der Website und der Anpassung der Inhalte an die Besucher.
              </p>
            </section>

            {/* 3. Art der Daten / Cookies */}
            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4 flex items-center gap-3" style={{ fontFamily: 'Playfair Display' }}>
                <span className="flex items-center justify-center w-8 h-8 rounded-full bg-sky-100 text-sky-600 text-sm font-bold">3</span>
                Art der Daten / Cookies
              </h2>
              <p className="text-slate-600 mb-3">
                Google Analytics verwendet sogenannte „Cookies". Das sind Textdateien, die auf Ihrem Endgerät gespeichert werden und eine Analyse der Benutzung der Website ermöglichen.
              </p>
              <p className="text-slate-600">
                Die durch Cookies erzeugten Informationen über die Nutzung dieser Website werden in der Regel an einen Server von Google übertragen und dort gespeichert.
              </p>
            </section>

            {/* 4. IP-Anonymisierung */}
            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4 flex items-center gap-3" style={{ fontFamily: 'Playfair Display' }}>
                <span className="flex items-center justify-center w-8 h-8 rounded-full bg-sky-100 text-sky-600 text-sm font-bold">4</span>
                IP-Anonymisierung
              </h2>
              <p className="text-slate-600 mb-3">
                Google Analytics wird auf dieser Website ausschließlich mit der Erweiterung „_anonymizeIp()" verwendet. Dadurch wird Ihre IP-Adresse von Google innerhalb von Mitgliedstaaten der Europäischen Union oder in anderen Vertragsstaaten des Abkommens über den Europäischen Wirtschaftsraum gekürzt.
              </p>
              <p className="text-slate-600 mb-3">
                Eine direkte Personenbeziehbarkeit wird dadurch ausgeschlossen.
              </p>
              <p className="text-slate-600">
                Die im Rahmen von Google Analytics übermittelte IP-Adresse wird nicht mit anderen Daten von Google zusammengeführt.
              </p>
            </section>

            {/* 5. Verarbeitung im Auftrag */}
            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4 flex items-center gap-3" style={{ fontFamily: 'Playfair Display' }}>
                <span className="flex items-center justify-center w-8 h-8 rounded-full bg-sky-100 text-sky-600 text-sm font-bold">5</span>
                Verarbeitung im Auftrag / Einbindung von Dienstleistern
              </h2>
              <p className="text-slate-600 mb-3">
                Google verarbeitet die erhobenen Daten im Auftrag des Websitebetreibers, um:
              </p>
              <ul className="list-disc list-inside text-slate-600 mb-4 space-y-1">
                <li>die Nutzung der Website auszuwerten</li>
                <li>Reports über Websiteaktivitäten zu erstellen</li>
                <li>weitere mit der Websitenutzung verbundene Dienstleistungen bereitzustellen</li>
              </ul>
              <p className="text-slate-600">
                Der technische Dienstleister Emergent kann im Rahmen der Bereitstellung der Website Zugriff auf Server-Logfiles erhalten. Die Verarbeitung erfolgt ausschließlich zur Bereitstellung und zum sicheren Betrieb der Website.
              </p>
            </section>

            {/* 6. Drittlandtransfer */}
            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4 flex items-center gap-3" style={{ fontFamily: 'Playfair Display' }}>
                <span className="flex items-center justify-center w-8 h-8 rounded-full bg-sky-100 text-sky-600 text-sm font-bold">6</span>
                Drittlandtransfer
              </h2>
              <p className="text-slate-600 mb-3">
                Eine Übermittlung personenbezogener Daten in die USA kann nicht ausgeschlossen werden.
              </p>
              <p className="text-slate-600">
                Google LLC verpflichtet sich zur Einhaltung eines angemessenen Datenschutzniveaus auf Grundlage der Standardvertragsklauseln der Europäischen Kommission.
              </p>
            </section>

            {/* 7. Speicherdauer */}
            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4 flex items-center gap-3" style={{ fontFamily: 'Playfair Display' }}>
                <span className="flex items-center justify-center w-8 h-8 rounded-full bg-sky-100 text-sky-600 text-sm font-bold">7</span>
                Speicherdauer
              </h2>
              <p className="text-slate-600 mb-3">
                Die mit Cookies, Nutzerkennungen (z. B. User-ID) oder Werbe-IDs verknüpften Daten werden nach spätestens 50 Monaten automatisch gelöscht.
              </p>
              <p className="text-slate-600">
                Die Löschung erfolgt automatisch einmal monatlich.
              </p>
            </section>

            {/* 8. Widerspruch / Opt-Out */}
            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4 flex items-center gap-3" style={{ fontFamily: 'Playfair Display' }}>
                <span className="flex items-center justify-center w-8 h-8 rounded-full bg-sky-100 text-sky-600 text-sm font-bold">8</span>
                Widerspruch / Opt-Out
              </h2>
              <p className="text-slate-600 mb-3">
                Sie können die Speicherung der Cookies durch eine entsprechende Einstellung Ihrer Browser-Software verhindern.
              </p>
              <p className="text-slate-600 mb-3">
                Darüber hinaus können Sie die Erfassung der durch Cookies erzeugten Daten (inkl. Ihrer IP-Adresse) verhindern, indem Sie das folgende Browser-Plugin herunterladen und installieren:
              </p>
              <a 
                href="https://tools.google.com/dlpage/gaoptout" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sky-600 hover:text-sky-700 mb-3"
              >
                <ExternalLink className="w-4 h-4" />
                https://tools.google.com/dlpage/gaoptout
              </a>
              <p className="text-slate-600">
                Alternativ können Sie die Erfassung durch Google Analytics verhindern, indem Sie Ihre Cookie-Einstellungen auf dieser Website anpassen.
              </p>
            </section>

            {/* 9. Betroffenenrechte */}
            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4 flex items-center gap-3" style={{ fontFamily: 'Playfair Display' }}>
                <span className="flex items-center justify-center w-8 h-8 rounded-full bg-sky-100 text-sky-600 text-sm font-bold">9</span>
                Betroffenenrechte
              </h2>
              <p className="text-slate-600 mb-3">
                Sie haben im Rahmen der geltenden gesetzlichen Bestimmungen jederzeit das Recht auf:
              </p>
              <ul className="list-disc list-inside text-slate-600 mb-4 space-y-1">
                <li>Auskunft über Ihre gespeicherten Daten</li>
                <li>Berichtigung unrichtiger Daten</li>
                <li>Löschung Ihrer Daten</li>
                <li>Einschränkung der Verarbeitung</li>
                <li>Widerspruch gegen die Verarbeitung</li>
                <li>Datenübertragbarkeit (sofern anwendbar)</li>
              </ul>
              <p className="text-slate-600">
                Zur Ausübung Ihrer Rechte wenden Sie sich bitte an: <a href="mailto:info@groemitzereiszeit.de" className="text-sky-600 hover:underline">info@groemitzereiszeit.de</a>
              </p>
            </section>

            {/* 10. Weitere Informationen */}
            <section className="mb-6">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4 flex items-center gap-3" style={{ fontFamily: 'Playfair Display' }}>
                <span className="flex items-center justify-center w-8 h-8 rounded-full bg-sky-100 text-sky-600 text-sm font-bold">10</span>
                Weitere Informationen
              </h2>
              <p className="text-slate-600 mb-3">
                Weitere Informationen zum Datenschutz bei Google finden Sie unter:
              </p>
              <a 
                href="https://policies.google.com/privacy" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sky-600 hover:text-sky-700"
              >
                <ExternalLink className="w-4 h-4" />
                https://policies.google.com/privacy
              </a>
            </section>

            {/* Stand */}
            <div className="border-t border-slate-200 pt-6 mt-10">
              <p className="text-slate-500 text-sm text-center">
                (Stand: 2026)
              </p>
            </div>

          </CardContent>
        </Card>

        {/* Back Button */}
        <div className="text-center mt-8">
          <Link to="/">
            <Button className="h-12 px-8 rounded-full bg-sky-500 hover:bg-sky-600 text-white font-semibold">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Zurück zur Startseite
            </Button>
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-8 px-6 mt-16">
        <div className="max-w-7xl mx-auto text-center text-slate-500 text-sm">
          <p>© 2026 Grömitzer Eiszeit. Alle Rechte vorbehalten.</p>
        </div>
      </footer>
    </div>
  );
};

export default Datenschutz;
